import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { useLocalStorage } from "@vueuse/core";
import router from "@/router";
import type { TourProps } from "ant-design-vue";
import USER_API from "@/api/classes/user";
import { debounce } from "@/tools/debounce";
import { useUser } from "@/stores/user/user";

export interface CurrentTourState {
  currentStep: number;
  isOpened: boolean;
  passed: boolean;
}

export const getEmptyTourState = () => ({
  currentStep: 0,
  isOpened: false,
  passed: false,
});

export interface TourState {
  [tourName: string | symbol]: CurrentTourState;
}

export const useOnboardingTours = defineStore("onboardingTours", () => {
  const userStore = useUser();
  const tours = ref<Map<string | symbol, TourProps["steps"]>>(new Map());

  const tourState = useLocalStorage<TourState>(
    "onboarding-tours",
    {} as TourState,
    {
      deep: true,
      listenToStorageChanges: true,
      mergeDefaults: true,
    },
  );
  const isInitialStateLoading = ref(false);

  const isUserTourStateLoading = ref(false);

  const currentTourName = computed<string | symbol | undefined>(
    () =>
      router.currentRoute.value.meta?.tourName as string | symbol | undefined,
  );
  const currentTourSteps = computed<TourProps["steps"]>(() =>
    currentTourName.value ? tours.value.get(currentTourName.value) : undefined,
  );
  const currentTourState = computed<CurrentTourState>({
    get: () => {
      if (!currentTourName.value) {
        return getEmptyTourState();
      }
      if (!tourState.value[currentTourName.value]) {
        tourState.value[currentTourName.value] = getEmptyTourState();
      }
      return tourState.value[currentTourName.value] as CurrentTourState;
    },
    set: (value) => {
      if (!currentTourName.value) return;
      tourState.value[currentTourName.value] = value;
    },
  });

  const setTourSteps = (tourSteps?: TourProps["steps"] | null) => {
    if (!currentTourName.value) return;
    if (!tourSteps) {
      if (tours.value.has(currentTourName.value)) {
        tours.value.delete(currentTourName.value);
      }
      return;
    }
    tours.value.set(currentTourName.value, tourSteps);
  };

  const openTour = () => {
    if (currentTourName.value === "mainMenuTour" && !userStore.isUserOwner)
      return;
    if (!currentTourSteps.value?.length) return;
    if (currentTourState.value.passed) return;
    if (currentTourState.value.isOpened) return;
    currentTourState.value.isOpened = true;
  };

  const loadUserTourState = async (isInitial = false) => {
    isUserTourStateLoading.value = true;
    try {
      const response = await USER_API.getOnboardingTourState();
      if (response.data) {
        isInitialStateLoading.value = isInitial;
        tourState.value =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;
      }
    } catch (e) {
      console.error("Failed to load user tour state", e);
    } finally {
      isUserTourStateLoading.value = false;
    }
  };

  const saveUserTourState = async () => {
    if (!tourState.value) return;
    if (isInitialStateLoading.value) {
      isInitialStateLoading.value = false;
      return;
    }
    try {
      await USER_API.setOnboardingTourState(tourState.value);
    } catch (e) {
      console.error("Failed to save user tour state", e);
    }
  };

  watch(() => tourState.value, debounce(saveUserTourState, 500), {
    deep: true,
  });

  return {
    currentTourSteps,
    setTourSteps,
    isUserTourStateLoading,
    loadUserTourState,
    saveUserTourState,
    currentTourState,
    openTour,
  };
});
